// The RPC product's account API in willow-saas, under `/account/rpc` (session
// cookie). Shapes follow willow-saas `src/rpc/dashboard.rs` and `keys.rs`.
// Money is integer micro-dollars; units are the RPC's metered units.

import { takeBoot } from './boot';
import { saasCall } from './saas-client';

/** `card_required`: a postpaid plan with no card on file; its keys are refused until one is saved. */
export type KeyState = 'active' | 'exhausted' | 'suspended' | 'card_required';
export type PlanSource = 'none' | 'stripe' | 'balance';

export interface RpcPlan {
  id: string;
  display_name: string;
  monthly_micros: number;
  included_units: number;
  proof_credit_micros: number;
  requests_per_second: number;
  /** Price of units past the included ones; null: none can be bought. */
  unit_price_micros_per_million: number | null;
  overage_allowed: boolean;
  proofs: boolean;
  /** Whether the plan includes state methods (balances, storage, eth_call on
   *  Ethereum, forwarded transactions). Off: they answer -32601. */
  state_methods?: boolean;
  placeholder: boolean;
  /** A negotiated plan: usage runs below zero and is invoiced monthly, in
   *  arrears, to the card on file. saas leaves it out when false. */
  postpaid?: boolean;
  /** `requests`: every key on the plan is billed per request. saas leaves it
   *  out for `units`. */
  billing?: 'units' | 'requests';
}

export interface Caps {
  overage_enabled: boolean;
  /** null: no cap. */
  spend_cap_micros: number | null;
  period_spend_micros: number;
}

/** A plan's price for one method group (willow-saas `groups.rs`). */
export interface GroupPrice {
  id: string;
  name: string;
  note: string;
  micros_per_million: number;
  methods: string[];
}

/** What a plan charges: per million of `unit`, by method group, the rest at its default. */
export interface RateCard {
  unit: 'requests' | 'units';
  default_micros_per_million: number | null;
  /** Empty: every request at the default price. */
  groups: GroupPrice[];
}

/** Usage charged at one price and not invoiced yet; `group_id` null: the default price. */
export interface OpenLine {
  group_id: string | null;
  name: string | null;
  micros_per_million: number;
  unit: string;
  quantity: number;
  charged_micros: number;
}

export interface RpcSummary {
  account_id: string;
  state: KeyState;
  plan: RpcPlan;
  plan_source: PlanSource;
  cancel_at_period_end: boolean;
  period: { start: string; end: string };
  units: { included: number; used: number; remaining: number };
  balance: { cash_micros: number; proof_credit_micros: number };
  caps: Caps;
  billing: {
    stripe_subscription_id: string | null;
    /** A card is saved for a postpaid plan's invoices (or from a subscription). */
    card_on_file: boolean;
    payment_failed_at: string | null;
    grace_ends_at: string | null;
    suspended_at: string | null;
    suspended_reason: string | null;
    overage_invoice_id: string | null;
  };
  /** Left out by a saas older than prices by method group. */
  rates?: RateCard;
  /** Postpaid plans: usage not invoiced yet, by price. */
  uninvoiced?: { lines: OpenLine[]; charged_micros: number } | null;
}

export interface RpcKey {
  key_id: string;
  name: string;
  prefix: string;
  sha256: string;
  requests_per_second: number | null;
  /** null: every chain. */
  chains: number[] | null;
  /** null: any origin. */
  allowed_origins: string[] | null;
  proofs_enabled: boolean;
  max_blocks_per_job: number | null;
  max_open_jobs: number | null;
  billing: string;
  created_at: string;
  revoked_at: string | null;
}

/** A key as it comes back from create or rotate: the only time `key` is shown. */
export interface MintedKey extends RpcKey {
  key: string;
}

/** On create, absent and null both mean "use the default". On update, absent
 *  leaves a field alone and null clears it. */
export interface KeyInput {
  name?: string;
  requests_per_second?: number | null;
  chains?: number[] | null;
  allowed_origins?: string[] | null;
  proofs_enabled?: boolean;
  max_blocks_per_job?: number | null;
  max_open_jobs?: number | null;
}

export interface UsageTotals {
  served_requests: number;
  served_units: number;
  refused: number;
  proof_served: number;
  billed_units: number;
  charged_micros: number;
}

export interface UsageRow extends UsageTotals {
  day: string;
  key_id: string;
  chain_id: number;
  written_off_micros: number;
}

/** Served usage of one method; `method` null: the RPC sent no method, `group_id` null: the default price. */
export interface MethodUsage {
  method: string | null;
  group_id: string | null;
  served_requests: number;
  served_units: number;
  charged_micros: number;
}

export interface GroupUsage {
  group_id: string | null;
  name: string | null;
  served_requests: number;
  served_units: number;
  charged_micros: number;
}

export interface UsageResponse {
  from: string;
  to: string;
  rows: UsageRow[];
  by_key: Record<string, UsageTotals>;
  /** Keyed by chain ID as a string. */
  by_chain: Record<string, UsageTotals>;
  /** Keyed by day, YYYY-MM-DD. */
  by_day: Record<string, UsageTotals>;
  total: UsageTotals;
  /** Left out by a saas older than prices by method group. */
  by_method?: MethodUsage[];
  by_group?: GroupUsage[];
}

export interface LedgerEntry {
  id: number;
  pocket: 'cash' | 'proof_credit';
  kind: string;
  amount_micros: number;
  balance_after_micros: number;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface LedgerResponse {
  entries: LedgerEntry[];
  next_before_id: number | null;
  unposted_usage_micros: number;
}

export interface CheckoutResult {
  status: string;
  url?: string;
  plan_id?: string;
  plan_source?: PlanSource;
  period_end?: string;
  balance_micros?: number;
}

/** What a plan meters, in words: "requests" or "units". */
export function meteredBy(plan: RpcPlan): 'requests' | 'units' {
  return plan.billing === 'requests' ? 'requests' : 'units';
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

type KeyList = { keys: RpcKey[] };

/** saas lists revoked keys after the active ones, so usage by them can be named. */
export const activeKeys = (keys: RpcKey[]): RpcKey[] => keys.filter((k) => !k.revoked_at);

export const rpcApi = {
  // The first read on an account page load takes what index.html started.
  summary: () => takeBoot<RpcSummary>('rpc') ?? saasCall<RpcSummary>('/rpc'),

  listKeys: async () =>
    (await (takeBoot<KeyList>('keys') ?? saasCall<KeyList>('/rpc/keys'))).keys,
  createKey: (input: KeyInput) =>
    saasCall<MintedKey>('/rpc/keys', { method: 'POST', ...json(input) }),
  updateKey: (id: string, input: KeyInput) =>
    saasCall<RpcKey>(`/rpc/keys/${encodeURIComponent(id)}`, { method: 'PATCH', ...json(input) }),
  revokeKey: (id: string) =>
    saasCall<null>(`/rpc/keys/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  rotateKey: (id: string) =>
    saasCall<MintedKey>(`/rpc/keys/${encodeURIComponent(id)}/rotate`, { method: 'POST' }),

  usage: (q: { from?: string; to?: string; key_id?: string; chain?: number }) => {
    const p = new URLSearchParams();
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    if (q.key_id) p.set('key_id', q.key_id);
    if (q.chain != null) p.set('chain', String(q.chain));
    const qs = p.toString();
    return saasCall<UsageResponse>(`/rpc/usage${qs ? `?${qs}` : ''}`);
  },

  ledger: (q: { limit?: number; before_id?: number | null } = {}) => {
    const p = new URLSearchParams();
    if (q.limit) p.set('limit', String(q.limit));
    if (q.before_id != null) p.set('before_id', String(q.before_id));
    const qs = p.toString();
    return saasCall<LedgerResponse>(`/rpc/ledger${qs ? `?${qs}` : ''}`);
  },

  putCaps: (caps: { overage_enabled?: boolean; spend_cap_micros?: number | null }) =>
    saasCall<Caps>('/rpc/caps', { method: 'PUT', ...json(caps) }),

  buyCredits: (amount_micros: number) =>
    saasCall<CheckoutResult>('/rpc/credits', { method: 'POST', ...json({ amount_micros }) }),
  choosePlan: (plan_id: string, funding: 'card' | 'balance') =>
    saasCall<CheckoutResult>('/rpc/plan', { method: 'POST', ...json({ plan_id, funding }) }),
  cancelPlan: () => saasCall<unknown>('/rpc/plan/cancel', { method: 'POST' }),
  resumePlan: () => saasCall<unknown>('/rpc/plan/resume', { method: 'POST' }),
  /** Postpaid plans only: a Stripe setup Checkout that saves the card the
   *  monthly invoice is charged to. Back at `/account?rpc=card_saved`. */
  saveCard: () => saasCall<CheckoutResult>('/rpc/card', { method: 'POST' }),

  billingPortal: () => saasCall<{ url: string }>('/billing-portal', { method: 'POST' }),
};

export type OAuthProvider = 'github' | 'google';

/** Full-page navigation target that starts GitHub or Google sign-in. saas
 *  lands the browser on `/account?oauth=ok`, or `/login?oauth_error=<code>`. */
export function oauthStartUrl(provider: OAuthProvider): string {
  return `/account/oauth/${provider}/start`;
}

/** Sign-in providers this saas deployment has configured
 *  (`GET /account/oauth/providers`); unconfigured ones are left out. */
export async function oauthProviders(): Promise<OAuthProvider[]> {
  const body = await saasCall<{ providers?: { id: string }[] }>('/oauth/providers');
  return (body?.providers ?? [])
    .map((p) => p.id)
    .filter((id): id is OAuthProvider => id === 'github' || id === 'google');
}

/** Plain-words messages for saas's `oauth_error` codes (see willow-saas oauth.rs). */
export function oauthErrorMessage(code: string): string {
  switch (code) {
    case 'denied':
      return 'Sign-in was cancelled.';
    case 'missing_code':
    case 'state_mismatch':
    case 'state_expired':
      return 'That sign-in attempt expired. Try again.';
    case 'verified_email_required':
      return 'Your GitHub or Google account needs a verified email address to sign in here.';
    case 'provider_unavailable':
    case 'provider_error':
    case 'token_exchange_failed':
      return "GitHub or Google didn't answer. Try again in a moment.";
    case 'unknown_provider':
      return "That sign-in option isn't available.";
    default:
      return 'Sign-in failed on our side. Try again.';
  }
}

/** Words for ledger entry kinds (willow-saas `ledger.rs`, `billing.rs`, `x402.rs`). */
export function ledgerKindLabel(kind: string): string {
  switch (kind) {
    case 'usage':
      return 'Usage';
    case 'plan_fee':
      return 'Plan fee';
    case 'proof_charge':
      return 'Proofs';
    case 'proof_refund':
      return 'Proof refund (job failed)';
    case 'proof_credit':
      return 'Monthly proof credit';
    case 'proof_credit_expired':
      return 'Unused proof credit expired';
    case 'proof_credit_plan_change':
      return 'Proof credit changed with plan';
    case 'stripe_credit':
      return 'Credit added (card)';
    case 'x402_credit':
      return 'Credit added (USDC)';
    case 'stripe_overage_payment':
      return 'Overage paid';
    case 'overage_forgiven':
      return 'Overage written off';
    default:
      return kind.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  }
}
